import type { FunctionComponent } from "react";
import { useEffect, useMemo, useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import { SWReactIcons } from "@similarweb/icons";
import type { INavItemBadgeType } from "@similarweb/ui-components";
import { PlainTooltip, IconButton, NavItemBadge } from "@similarweb/ui-components";

import { EAvailabilityMode } from "app/common/services/pageClaims";
import { swSettings } from "app/common/services/swSettings";
import { useEducationBarProminentContext } from "app/components/educationbar/useEducationBarProminentContext";
import { DownloadButtonMenu } from "app/components/React/DownloadButtonMenu/DownloadButtonMenu";
import { BADGE_ITEMS } from "app/components/sidebar3/types";
import { SubNavEducationBarProminentPopup } from "app/components/sun-nav-education-bar-prominent/SubNavEducationBarProminentPopup";
import { translate } from "app/filters/i18nFilter";
import { AlertButton } from "app/modules/project-from-comparison/components/AlertButton/AlertButton";
import {
    MODAL_ALERTS_HEADER,
    MODAL_ALERTS_SUBHEADER,
} from "app/modules/project-from-comparison/components/ProjectCreationModal/text";
import { ProjectCreation } from "app/modules/project-from-comparison/ProjectCreation";
import { FlexContainerCentered } from "app/pages/conversion/oss/ConversionSegmentOSSStyles";
import { getPageSettings } from "app/reducers/_reducers/pageSettingsReducer";
import { allTrackers } from "app/services/track/track";
import { getPDFCallback } from "app/UtilitiesAndConstants/UtilityFunctions/getPDF";
import { isTrialOrAfterTrial } from "app/UtilitiesAndConstants/UtilityFunctions/trialUsersUtils";

import { DashboardLoader } from "../DashboardLoader";
import { PDFButton } from "../PDFButton/PDFButton";

import {
    EducationIconStyle,
    IconButtonStyled,
    PageTitleStyle,
    TitleContainer,
    TitleStyle,
    TooltipStyle,
} from "./styled";
import { calculateWidthToReduce } from "./utils";

interface IPageTitleProps {
    title?: string;
    className?: string;
    onEducationIconClick?: () => void;
    showEducationIcon?: boolean;
    badgeType?: INavItemBadgeType;
    badgeText?: string;
    isDisabled?: boolean;
}

const PageTitle: FunctionComponent<IPageTitleProps> = (props) => {
    const { state, params } = useCurrentStateAndParams();
    const {
        pageTitle = "",
        pageTitleTooltip,
        pageSubtitle,
        isPdfDownloadButton = false,
        hasExcelDownloadButton = false,
        orangeBadgeTitle,
        GreenBadgeTitleComponent,
        pinkBadgeTitle,
        dataBadgeTitle,
        dataBadgeTooltip,
        pdfDownloadsMethod = async () => void 0,
        excelDownloadsMethod,
        pinkBadgeTooltip,
        allowHoverDataBadgeTooltip,
        closeDelayBadgeTooltip,
        downloadExcelPermitted,
        customPdfComponent: CustomPdfComponent,
    } = useMemo(() => getPageSettings(state, params), [state, params]);
    const { showEducationIcon = false, onEducationIconClick, badgeType, badgeText } = props;
    const [isPdfLoading, setIsPdfLoading] = useState<boolean>(false);
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const { isMinimized, isProminentEducationBannerShown } = useEducationBarProminentContext();

    const isDownloadPdfHook = swSettings.current?.resources?.IsExportToPDFAllowed === EAvailabilityMode.HOOK;
    const isDownloadPdfHidden = swSettings.current?.resources?.IsExportToPDFAllowed === EAvailabilityMode.HIDDEN;
    const widthToReduce = calculateWidthToReduce(showEducationIcon, isPdfDownloadButton, hasExcelDownloadButton);

    const onEducationClick = () => {
        allTrackers.dispatchTrackEvent("Button", "click", "Header/Education/open");
        onEducationIconClick?.();
    };

    const onClickDownloadPdf = getPDFCallback(
        setIsPdfLoading,
        pdfDownloadsMethod as () => Promise<void>,
        isDownloadPdfHook,
    );

    useEffect(() => {
        const onBeforePrint = () => {
            allTrackers.dispatchTrackEvent("button", "print screen", "PDF");
        };

        window.addEventListener("beforeprint", onBeforePrint);

        return () => {
            window.removeEventListener("beforeprint", onBeforePrint);
        };
    }, []);

    const onClickDownloadExcel = async () => {
        allTrackers.dispatchTrackEvent("Download", "submit-ok", "excel");
        return excelDownloadsMethod?.();
    };

    const pdfComponent = () => {
        if (isTrialOrAfterTrial()) {
            return (
                <IconButton
                    iconName="download"
                    type="flat"
                    onClick={onClickDownloadPdf}
                    isDisabled={props.isDisabled}
                >
                    {translate("trial.pdf.icon.text.vwo")}
                </IconButton>
            );
        }

        return (
            <PDFButton
                isDisabled={props.isDisabled || isPdfLoading}
                onClick={onClickDownloadPdf}
                isLocked={isDownloadPdfHook}
            />
        );
    };

    const pageTitleTooltipValue = pageTitleTooltip ?? props.title ?? pageTitle;

    return (
        <PageTitleStyle
            className={props.className}
            widthToReduce={widthToReduce}
        >
            <TitleContainer>
                <PlainTooltip
                    tooltipContent={translate(pageTitleTooltipValue)}
                    enabled={!!pageTitleTooltipValue}
                    placement={"bottom"}
                >
                    <TitleStyle data-automation="page-title">{translate(props.title ?? pageTitle)}</TitleStyle>
                </PlainTooltip>
                {pageSubtitle ? (
                    <TooltipStyle>
                        <PlainTooltip tooltipContent={translate(pageSubtitle)}>
                            <div>
                                <SWReactIcons
                                    iconName="info"
                                    size="xs"
                                />
                            </div>
                        </PlainTooltip>
                    </TooltipStyle>
                ) : null}
                {orangeBadgeTitle ? (
                    <NavItemBadge
                        type={BADGE_ITEMS.NEW}
                        text={badgeText}
                    />
                ) : null}
                {pinkBadgeTitle ? (
                    pinkBadgeTooltip ? (
                        <PlainTooltip
                            tooltipContent={pinkBadgeTooltip}
                            placement={"bottom"}
                        >
                            <div>
                                <NavItemBadge
                                    type={BADGE_ITEMS.BETA}
                                    text={pinkBadgeTitle}
                                />
                            </div>
                        </PlainTooltip>
                    ) : (
                        <NavItemBadge
                            type={BADGE_ITEMS.BETA}
                            text={badgeText}
                        />
                    )
                ) : null}
                {badgeType && badgeText ? (
                    <NavItemBadge
                        type={badgeType}
                        text={badgeText}
                    />
                ) : null}
                {dataBadgeTitle ? (
                    dataBadgeTooltip ? (
                        <PlainTooltip
                            tooltipContent={dataBadgeTooltip}
                            enabled={true}
                            placement={"bottom"}
                            closeDelay={closeDelayBadgeTooltip}
                            allowHover={allowHoverDataBadgeTooltip}
                        >
                            <div>
                                <NavItemBadge
                                    type={BADGE_ITEMS.DATA20}
                                    text={dataBadgeTitle}
                                />
                            </div>
                        </PlainTooltip>
                    ) : (
                        <NavItemBadge
                            type={BADGE_ITEMS.DATA20}
                            text={badgeText}
                        />
                    )
                ) : null}

                {GreenBadgeTitleComponent ? <GreenBadgeTitleComponent /> : null}
            </TitleContainer>

            <FlexContainerCentered>
                <ProjectCreation
                    modalTitle={MODAL_ALERTS_HEADER}
                    modalSubTitle={MODAL_ALERTS_SUBHEADER}
                    renderButton={(onClick: () => void) => <AlertButton onClick={onClick} />}
                />

                {!isDownloadPdfHidden && isPdfDownloadButton ? (
                    CustomPdfComponent ? (
                        <CustomPdfComponent setIsPdfLoading={setIsPdfLoading} />
                    ) : (
                        pdfComponent()
                    )
                ) : null}
                {hasExcelDownloadButton ? (
                    <DownloadButtonMenu
                        Excel={true}
                        excelLocked={!downloadExcelPermitted}
                        handleDownloadFunction={onClickDownloadExcel}
                        buttonText={"btn.download.xls"}
                    />
                ) : null}
                {showEducationIcon ? (
                    <>
                        {isProminentEducationBannerShown && isMinimized ? (
                            <SubNavEducationBarProminentPopup
                                isPopupOpen={isPopupOpen}
                                setIsPopupOpen={setIsPopupOpen}
                            >
                                <PlainTooltip
                                    enabled={!isPopupOpen}
                                    tooltipContent={translate("growth.page.title.education.prominent.icon.tooltip")}
                                >
                                    <EducationIconStyle>
                                        <IconButtonStyled
                                            onClick={onEducationClick}
                                            type="flat"
                                            iconName="learning-hat"
                                            iconSize="sm"
                                        />
                                    </EducationIconStyle>
                                </PlainTooltip>
                            </SubNavEducationBarProminentPopup>
                        ) : null}
                        {!isProminentEducationBannerShown ? (
                            <PlainTooltip tooltipContent={translate("page.title.education.icon.tooltip")}>
                                <EducationIconStyle>
                                    <IconButtonStyled
                                        onClick={onEducationClick}
                                        type="flat"
                                        iconName="learning-hat"
                                        iconSize="sm"
                                    />
                                </EducationIconStyle>
                            </PlainTooltip>
                        ) : null}
                    </>
                ) : null}
            </FlexContainerCentered>
            {isPdfLoading ? (
                <DashboardLoader
                    title={translate("common.pdfoverlay.generatingreport")}
                    subtitle={translate("common.pdfoverlay.downloadedshortly")}
                />
            ) : null}
        </PageTitleStyle>
    );
};

export default PageTitle;
